// Dependencies
import { createContext, FC, ReactNode, useMemo, useState, useRef } from 'react';

// Resources
import {
  DisplayMode,
  IAccount,
  ICustomerDocument,
  IDocument,
  IEsgAreaDocument,
  IFilter,
  IPagination, IRetentionConfig,
  ITable,
  ROLE
} from '../resource';

// Apis
import apis from '../apis';
import {useAlertContext} from '../components/Alert/alertContext';
import {useToastContext} from '../components/Toast/toastContext';

const DEFAULT_DAYS = 30;

// Interfaces
interface IAppProviderProps {
  children: ReactNode;
}

export interface IAppContext {
  isAdmin: boolean,
  account: IAccount | null;
  setAccount: (account: IAccount | null) => void;
  retentionConfig: IRetentionConfig;
  setRetentionConfig: (value: IRetentionConfig) => void;
  search: string;
  setSearch: (search: string) => void;
  displayMode: DisplayMode;
  setDisplayMode: (displayMode: DisplayMode) => void;
  showDeleted: boolean;
  setShowDeleted: (showDeleted: boolean) => void;
  showBackups: boolean;
  setShowBackups: (showBackups: boolean) => void;
  dialogs: {
    [key: string]: {
      visible: boolean;
      setVisible: (visible: boolean) => void;
    };
  };
  chart: {
    table: {
      value: ITable;
      setValue: (value: ITable) => void;
    };
  };
  getMe: () => void;
  document: any,
  setDocument: (value: any) => void;
  documents: IDocument[];
  docTotalCount: number;
  setDocTotalCount: (value: number) => void;
  documentsLoading: boolean;
  setDocuments: (documents: IDocument[]) => void;
  fetchDocuments: (id: string, page?: number, limit?: number, showDeleted?: boolean, sortField?: string, sortOrder?: string, search?: string, callback?: (msg?: string) => void, signal?: AbortSignal, requestId?: number) => void;
  user: {
    value?: IAccount;
    setValue: (user?: IAccount) => void;
  };
  users: {
    value: IAccount[];
    userToConfig: IAccount | undefined;
    setUserToConfig: (users: IAccount) => void;
    setValue: (users: IAccount[]) => void;
    isLoading: boolean;
    pagination?: IPagination;
    fetchData: (filter?: IFilter) => void;
    fetchUser: (id: string) => void;
    updateUser: (id: string, data: IAccount) => void;
    saveUserConfig?: () => void;
    setSaveUserConfig?: (handler: (() => void) | undefined) => void;
    isSavingUserConfig?: boolean;
    setIsSavingUserConfig?: (isSaving: boolean) => void;
  };
  selectedUsers: {
    value: string[];
    setValue: (users: string[]) => void;
  };
  customerDoc: {
    value?: ICustomerDocument;
    setValue: (doc?: ICustomerDocument) => void;
  };
  customerDocs: {
    value: ICustomerDocument[];
    setValue: (docs: ICustomerDocument[]) => void;
    selected: string[];
    setSelected: (docs: string[]) => void;
    isLoading: boolean;
    pagination?: IPagination;
    fetchData: (customerId: string, filter?: IFilter & { signal?: AbortSignal; requestId?: number }) => void;
  };
  esgDoc: {
    value?: ICustomerDocument;
    setValue: (doc?: ICustomerDocument) => void;
  };
  esgDocs: {
    value: ICustomerDocument[];
    setValue: (docs: ICustomerDocument[]) => void;
    selected: string[];
    setSelected: (docs: string[]) => void;
    isLoading: boolean;
    pagination?: IPagination;
    fetchData: (customerId: string, filter?: IFilter) => void;
    esgDocAllowed: boolean;
    setEsgDocAllowed: (value: boolean) => void;
  };
  esgAreaDocs: {
    value: IEsgAreaDocument[];
    setValue: (docs: IEsgAreaDocument[]) => void;
    isLoading: boolean;
    pagination?: IPagination;
    fetchData: (customerId: string, filter?: IFilter) => void;
  };
}

// Export app context
export const AppContext = createContext<IAppContext>({} as IAppContext);

// Create context provider
const AppProvider: FC<IAppProviderProps> = ({ children }) => {
  // States
  const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.Module);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [account, setAccount] = useState<IAccount | null>(null);
  const [visibleLoginDialog, setVisibleLoginDialog] = useState<boolean>(false);
  const [visibleNewDocDialog, setVisibleNewDocDialog] = useState<boolean>(false);
  const [visibleUserDialog, setVisibleUserDialog] = useState<boolean>(false);
  const [tableValue, setTableValue] = useState<{ row: number; column: number }>({ row: 0, column: 0 });
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [docTotalCount, setDocTotalCount] = useState(0);
  const [documentsLoading, setDocumentsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<IAccount[]>([]);
  const [userToConfig, setUserToConfig] = useState<IAccount>();
  const [usersPagination, setUsersPagination] = useState<IPagination>();
  const [isUsersLoading, setIsUsersLoading] = useState<boolean>(false);
  const [saveUserConfigHandler, setSaveUserConfigHandler] = useState<(() => void) | undefined>(undefined);
  const [isSavingUserConfig, setIsSavingUserConfig] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [customerDocs, setCustomerDocs] = useState<ICustomerDocument[]>([]);
  const [isCustomerDocsLoading, setIsCustomerDocsLoading] = useState<boolean>(false);
  const [customerDocsPagination, setCustomerDocsPagination] = useState<IPagination>();
  const [selectedCustomerDocs, setSelectedCustomerDocs] = useState<string[]>([]);
  const [customerDoc, setCustomerDoc] = useState<ICustomerDocument>();
  const [esgDocs, setEsgDocs] = useState<ICustomerDocument[]>([]);
  const [isEsgDocsLoading, setIsEsgDocsLoading] = useState<boolean>(false);
  const [esgDocsPagination, setEsgDocsPagination] = useState<IPagination>();
  const [selectedEsgDocs, setSelectedEsgDocs] = useState<string[]>([]);
  const [esgDoc, setEsgDoc] = useState<ICustomerDocument>();
  const [esgDocAllowed, setEsgDocAllowed] = useState<boolean>(false);
  const [esgAreaDocs, setEsgAreaDocs] = useState<IEsgAreaDocument[]>([]);
  const [isEsgAreaDocsLoading, setIsEsgAreaDocsLoading] = useState<boolean>(false);
  const [esgAreaDocsPagination, setEsgAreaDocsPagination] = useState<IPagination>();
  const [user, setUser] = useState<IAccount>();
  const [visibleCustomerDocDialog, setVisibleCustomerDocDialog] = useState<boolean>(false);
  const [visibleEsgDocDialog, setVisibleEsgDocDialog] = useState<boolean>(false);
  const [visibleEsgAreaDialog, setVisibleEsgAreaDialog] = useState<boolean>(false);
  const [document, setDocument] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [retentionConfig, setRetentionConfig] = useState<IRetentionConfig>({
    document: DEFAULT_DAYS,
    user: DEFAULT_DAYS,
    customerDocument: DEFAULT_DAYS,
    template: DEFAULT_DAYS,
    esg: DEFAULT_DAYS,
    widget: DEFAULT_DAYS,
  });
  const latestRequestId = useRef(0);
  const currentRequestId = useRef(0);

  const isAdmin = useMemo(() => account?.role === ROLE.ADMIN, [account])

  // Define value
  const value = {
    isAdmin,
    account,
    setAccount,
    displayMode,
    setDisplayMode,
    showDeleted,
    setShowDeleted: (value: boolean) => {
      console.log('setShowDeleted called with:', value);
      setShowDeleted(value);
      if (value) setShowBackups(false);
    },
    showBackups,
    setShowBackups: (value: boolean) => {
      setShowBackups(value);
      if (value) setShowDeleted(false);
    },
    search,
    setSearch,
    retentionConfig,
    setRetentionConfig,
    getMe: async () => {
      apis.auth
        .me()
        .then((res) => {
          setAccount(res.user);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    dialogs: {
      login: {
        visible: visibleLoginDialog,
        setVisible: setVisibleLoginDialog,
      },
      newDoc: {
        visible: visibleNewDocDialog,
        setVisible: setVisibleNewDocDialog,
      },
      user: {
        visible: visibleUserDialog,
        setVisible: setVisibleUserDialog,
      },
      customerDoc: {
        visible: visibleCustomerDocDialog,
        setVisible: setVisibleCustomerDocDialog,
      },
      esgDoc: {
        visible: visibleEsgDocDialog,
        setVisible: setVisibleEsgDocDialog,
      },
      esgArea: {
        visible: visibleEsgAreaDialog,
        setVisible: setVisibleEsgAreaDialog,
      }
    },
    chart: {
      table: {
        value: tableValue,
        setValue: setTableValue,
      },
    },
    document,
    setDocument,
    documents,
    setDocuments,
    docTotalCount,
    setDocTotalCount,
    documentsLoading,
    fetchDocuments: async (id: string, page?: number, limit?: number, showDeleted?: boolean, sortField?: string, sortOrder?: string, search?: string, callback?: (msg?: string) => void, signal?: AbortSignal, requestId?: number) => {
      setDocumentsLoading(true);
      if (requestId) {
        currentRequestId.current = requestId;
      }
      const fetch = account?.role === ROLE.ADMIN ? apis.pdf.getAllDocuments : apis.pdf.getDocuments;

      const options: any = { 
        page, 
        limit, 
        archived: showDeleted ? 1 : 0,
        backups: showBackups ? 1 : 0,
      };

      // Add search parameter if provided
      if (search) {
        options.search = search;
      }

      // Add sort parameters if provided
      if (sortField && sortOrder) {
        options.sort = {
          [sortField]: sortOrder
        };
      }

      const params = { options };

      fetch(id as string, params, signal)
        .then((res) => {
          setDocuments(res.results);
          setDocTotalCount(res.totalCount);
        })
        .catch((error) => {
          if (error && error.canceled) {
            // Request was cancelled, do nothing
            return;
          }
          callback && callback(error.msg);
          console.log(error);
        })
        .finally(() => {
          // Only set loading to false if this is still the current request
          if (!requestId || requestId === currentRequestId.current) {
            setDocumentsLoading(false);
          }
        });
    },
    user: {
      value: user,
      setValue: setUser,
    },
    users: {
      value: users,
      setValue: setUsers,
      isLoading: isUsersLoading,
      pagination: usersPagination,
      userToConfig,
      setUserToConfig: setUserToConfig,
      fetchData: (filter?: IFilter & { signal?: AbortSignal; requestId?: number }) => {
        const { signal, requestId, ...filterOptions } = filter || {};
        
        if (requestId) {
          currentRequestId.current = requestId;
        }
        
        setIsUsersLoading(true);
        apis.user
          .getUsers(filterOptions, { signal })
          .then((res) => {
            setUsers(res.users);
            setUsersPagination(res.pagination);
          })
          .catch((err) => {
            if (err?.name === 'CanceledError' || err?.message?.includes('canceled') || err?.canceled) {
              // Request was cancelled, do nothing
              return;
            }
            console.log(err);
          })
          .finally(() => {
            // Only set loading to false if this is still the current request
            if (!requestId || requestId === currentRequestId.current) {
              setIsUsersLoading(false);
            }
          });
      },
      fetchUser: (id: string) => {
        setIsUsersLoading(true);
        apis.user.getUser(id)
          .then((res) => {
            setUserToConfig(res);
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            setIsUsersLoading(false);
          })
      },
      updateUser: (id: string, data: IAccount) => {
        apis.user.updateUser(id, data)
          .then((res) => {
            setUserToConfig(data);
          })
          .catch((err) => {
            console.log(err);
          })
      },
      saveUserConfig: saveUserConfigHandler,
      setSaveUserConfig: (handler: (() => void) | undefined) => {
        setSaveUserConfigHandler(handler);
      },
      isSavingUserConfig: isSavingUserConfig,
      setIsSavingUserConfig: (isSaving: boolean) => {
        setIsSavingUserConfig(isSaving);
      }
    },
    selectedUsers: {
      value: selectedUsers,
      setValue: setSelectedUsers,
    },
    customerDoc: {
      value: customerDoc,
      setValue: setCustomerDoc,
    },
    customerDocs: {
      value: customerDocs,
      setValue: setCustomerDocs,
      selected: selectedCustomerDocs,
      setSelected: setSelectedCustomerDocs,
      isLoading: isCustomerDocsLoading,
      pagination: customerDocsPagination,
      fetchData: (customerId: string, filter?: IFilter & { signal?: AbortSignal; requestId?: number }) => {
        const { signal, requestId, ...filterOptions } = filter || {};
        
        if (requestId) {
          currentRequestId.current = requestId;
        }
        
        setIsCustomerDocsLoading(true);

        const fetch = account?.role === ROLE.ADMIN ? apis.pdf.getAllCustomerDocuments : apis.pdf.getCustomerDocuments;

        fetch(customerId, filterOptions, signal)
          .then((res) => {
            setCustomerDocs(res.customerDocs);
            setCustomerDocsPagination(res.pagination);
          })
          .catch((err) => {
            if (err?.name === 'CanceledError' || err?.message?.includes('canceled') || err?.canceled) {
              // Request was cancelled, do nothing
              return;
            }
            console.log(err);
          })
          .finally(() => {
            // Only set loading to false if this is still the current request
            if (!requestId || requestId === currentRequestId.current) {
              setIsCustomerDocsLoading(false);
            }
          });
      },
    },
    esgDoc: {
      value: esgDoc,
      setValue: setEsgDoc,
    },
    esgDocs: {
      value: esgDocs,
      setValue: setEsgDocs,
      selected: selectedEsgDocs,
      setSelected: setSelectedEsgDocs,
      isLoading: isEsgDocsLoading,
      esgDocAllowed: esgDocAllowed,
      setEsgDocAllowed: setEsgDocAllowed,
      pagination: esgDocsPagination,
      fetchData: (customerId, filter) => {
        setIsEsgDocsLoading(true);

        apis.esg.getEsgDocuments(customerId, filter)
          .then((res) => {
            setEsgDocs(res.esgDocs);
            setEsgDocsPagination(res.pagination);
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            setIsEsgDocsLoading(false);
          });
      },
    },
    esgAreaDocs: {
      value: esgAreaDocs,
      setValue: setEsgAreaDocs,
      isLoading: isEsgAreaDocsLoading,
      pagination: esgAreaDocsPagination,
      fetchData: (docId, filter) => {
        setIsEsgAreaDocsLoading(true);

        const fetch = apis.esg.getCustomerAreas;

        fetch(docId, filter)
          .then((res) => {
            setEsgAreaDocs(res.data);
            setEsgAreaDocsPagination(res.pagintaion);
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            setIsEsgAreaDocsLoading(false);
          });
      }
    }
  };

  // Return app provider
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Export context provider
export default AppProvider;
